import { format, formatDate, formatDistanceToNow, type Locale } from 'date-fns';
import { useDateFnsLocale } from './use-date-fns-locale';

type FormatPreset =
  | 'long_date'
  | 'short_weekday'
  | 'long_weekday'
  | 'short_day_of_month'
  | 'month_day'
  | 'month_day_year'
  | 'month_year'
  | 'day_month_year'
  | 'week_year'
  | 'single_letter_weekday';

type PresetFormat = string | Record<string, string>;

const presetFormats: Record<FormatPreset, PresetFormat> = {
  long_date: 'PPPP',
  short_weekday: 'EEE',
  long_weekday: 'EEEE',
  single_letter_weekday: 'EEEEE',
  short_day_of_month: 'd',
  month_day: {
    fr: 'd MMM',
    default: 'MMM d',
  },
  month_day_year: {
    fr: 'd MMM yyyy',
    default: 'MMM d, yyyy',
  },
  month_year: {
    fr: 'MMM yyyy',
    default: 'MMM yyyy',
  },
  day_month_year: 'dd MMMM yyyy',
  week_year: {
    fr: 'w, yyyy',
    default: 'w, yyyy',
  },
};

export function formatWithPreset(
  value: Date | number | string,
  preset: FormatPreset,
  locale: Locale
) {
  const presetFormat = presetFormats[preset] ?? 'P';
  let formatStr: string;
  if (typeof presetFormat === 'object') {
    const localeCode = (locale as any).code || 'default';
    formatStr = presetFormat[localeCode] || presetFormat.default;
  } else {
    formatStr = presetFormat;
  }
  return formatDate(value, formatStr, { locale });
}

export function useFormatDate() {
  const locale = useDateFnsLocale();

  return {
    format: (date: Date | number | string, formatStr: string) => {
      return format(date, formatStr, { locale });
    },
    formatWithPreset: (date: Date | number | string, preset: FormatPreset) => {
      return formatWithPreset(date, preset, locale);
    },
    formatDistanceToNow: (
      date: Date | number | string,
      options?: {
        addSuffix?: boolean;
      }
    ) => {
      return formatDistanceToNow(date, { locale, ...options });
    },
  };
}
